import React, { useEffect, useRef } from 'react';
import throttle from 'lodash.throttle';
import useBreakpoint, { Breakpoints } from '@hooks/useBreakpoint';
import useObjectVisibility from '@hooks/useObjectVisibility';
import ProductAddToCartButton from '@components/product/ProductAddToCartButton';
import AddToCartCTA from '_components/Product/AddToCartCTA';
import ProductAddToCartButtonPersistent from '@components/product/ProductAddToCartButtonPersistent';
import { FEATURE_FLAG_PDP_2026_1 } from '@config/feature-flag';

import styles from './ProductAddToCartWrapper.module.scss';

interface ProductAddToCartButtonWrapperProps {
  prismicDealsBannerTitles?: string[];
}

const ProductAddToCartButtonWrapper = ({
  prismicDealsBannerTitles = [],
}: ProductAddToCartButtonWrapperProps) => {
  const addToCartButtonRef = useRef<HTMLDivElement | null>(null);
  const isMobile = useBreakpoint(Breakpoints.md);
  const staticAddToCartIsVisible = useObjectVisibility(
    addToCartButtonRef,
    0,
    false
  );

  useEffect(() => {
    const body = document.body;
    const defaultBottomPadding = body.style.paddingBottom;

    if (staticAddToCartIsVisible || !isMobile) {
      body.style.paddingBottom = defaultBottomPadding;
      return () => {
        body.style.paddingBottom = defaultBottomPadding;
      };
    }

    const updateBottomPadding = () => {
      const addToCartButtonHeight =
        addToCartButtonRef.current?.getBoundingClientRect().height ?? 0;
      // Reserve footer space so the persistent add-to-cart bar does not cover content at page bottom.
      body.style.paddingBottom = `${addToCartButtonHeight}px`;
    };

    const throttledUpdateBottomPadding = throttle(updateBottomPadding, 100);

    updateBottomPadding();
    window.addEventListener('resize', throttledUpdateBottomPadding);

    return () => {
      window.removeEventListener('resize', throttledUpdateBottomPadding);
      throttledUpdateBottomPadding.cancel();
      body.style.paddingBottom = defaultBottomPadding;
    };
  }, [isMobile, staticAddToCartIsVisible]);

  return (
    <>
      <div ref={addToCartButtonRef}>
        {FEATURE_FLAG_PDP_2026_1 ? (
          <div className={styles.addToCartCTAWrapper}>
            <AddToCartCTA prismicDealsBannerTitles={prismicDealsBannerTitles} />
          </div>
        ) : (
          <ProductAddToCartButton />
        )}
      </div>
      <ProductAddToCartButtonPersistent
        isVisible={!staticAddToCartIsVisible}
        prismicDealsBannerTitles={prismicDealsBannerTitles}
      />
    </>
  );
};

export default ProductAddToCartButtonWrapper;
